import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  activateDeactivateUser,
  downloadUsers,
  fetchRecommendations,
  getAllUsers,
  updateUser,
} from "../../Services/user";
import {
  getActiveMasterTag, updateUserTag
} from "../../Services/tags"
import CustomTable from "../../Component/Table";
import FilterSelect from "../../Component/FilterSelect";
import { FilterContainer } from "../../Styles/filterStyle";
import { Button, Flex, Modal, Tag, Dropdown, Menu, DatePicker } from "antd";
import { EditOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { EyeOutlined, SolutionOutlined, FormOutlined, FileSearchOutlined, DeploymentUnitOutlined, EllipsisOutlined, UserOutlined, TagsOutlined, GiftOutlined } from "@ant-design/icons";
import EditUserModal from "./EditUserModal";
import { useSelector } from "react-redux";
import { fetchCities, fetchStates, fetchCountries } from "../../Services/user";
import { formatDate } from "../../utils/date";
import DebouncedInput from "../../Component/DebouncedInput";
import ViewModal from "./ViewModal";
import PlanViewModal from "./PlanViewModal";
import PlanCappedModal from "./PlanCappedModal";
import PlanUpdateModal from "./PlanUpdateModal";
import LogViewModal from "./LogViewModal";
import UserTagModal from "./UserTagModal"
import AssignCouponModal from "./AssignCouponModal"
import MassAssignCouponModal from "./MassAssignCouponModal"

const { RangePicker } = DatePicker;

const TagsCell = ({ tags }) => {
  const [expanded, setExpanded] = React.useState(false);
  if (!tags || tags.length === 0) return null;

  const displayedTags = expanded ? tags : tags.slice(0, 3);
  const showToggle = tags.length > 3;

  return (
    <div style={{"width": 150, whiteSpace: "normal",
            overflowWrap: "break-word",
            textOverflow: "ellipsis",}}>
      {displayedTags.join(', ')}
      {showToggle && (
        <span
          onClick={() => setExpanded(!expanded)}
          style={{
            color: '#1890ff',
            marginLeft: 5,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          {expanded ? ' Show less' : ' ...View more'}
        </span>
      )}
    </div>
  );
};

const columns = (handleEditClick, handleToggleActive, handleViewClick, handlePlanViewClick, handlePlanCappedClick,
   handleLogClick, handlePlanUpgradeViewClick, handleSort, sortOrder, handleImageClick, handleTagClick, handleAssignCouponClick) => [
  {
    title: (
      <Flex gap="large" onClick={() => handleSort()} style={{ cursor: 'pointer' }}>
        User Id
        {sortOrder === 'desc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      </Flex>
    ),
    dataIndex: "id",
    key: "id",
    className: "small"
  },
  {
    title: "Full Name",
    key: "full_name",
    render: (_, record) => {
      const full = record.name || `${record.first_name || ""} ${record.last_name || ""}`.trim();
      return full || "-";
    },
  },
  {
    title: "First Name",
    dataIndex: "first_name",
    key: "first_name",
    render: (text) => (text ? text : "-"),
  },
  {
    title: "Last Name",
    dataIndex: "last_name",
    key: "last_name",
    render: (text) => (text ? text : "-"),
  },
  {
    title: "Profile Image",
    dataIndex: "full_profile_image_url",
    key: "full_profile_image_url",
    render: (image) => (
      image ? (
        <img
          src={image}
          alt="Profile"
          style={{ width: 50, height: 50, borderRadius: 5 }}
          onClick={() => handleImageClick(image)}  
        />
      ) : (
        "-"
      )
    )
  },
  {
    title: "Gender",
    dataIndex: "gender",
    key: "gender",
    render: (text) =>
                text
                ? text
                    .split('_') // split on underscores
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // capitalize each word
                    .join(' ') // join with space
                : "-",
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
  },
  {
    title: "Phone",
    dataIndex: "phone_number",
    key: "phone_number",
  },
  {
    title: "City",
    dataIndex: "city_name",
    key: "city",
    render: (text, record) => (text ? text : "-"),
  },
  {
    title: "State",
    dataIndex: "state_name",
    key: "state_name",
    render: (text, record) => (text ? text : "-"),
  },
  {
    title: "Country",
    dataIndex: "country_name",
    key: "country_name",
    render: (text, record) => (text ? text : "-"),
  },
  {
    title: "Currency",
    dataIndex: "currency",
    key: "currency",
    render: (text, record) => (text.currency ? text.currency_symbol+" / "+text.currency  : "-"),
  },
  {
    title: "User Tags",
    dataIndex: "tags",
    key: "tags",
    render: (tags) => <TagsCell tags={tags} />,
  },
  {
    title: "Email Verified",
    dataIndex: "is_email_verified",
    key: "is_email_verified",
    render: (text, record) => (
      <Tag color={record?.is_email_verified ? "green" : "red"}>
        {record?.is_email_verified ? "Yes" : "No"}
      </Tag>
    ),
  },
  {
    title: "Mobile Verified",
    dataIndex: "is_mobile_verified",
    key: "is_mobile_verified",
    render: (text, record) => (
      <Tag color={record?.is_mobile_verified ? "green" : "red"}>
        {record?.is_mobile_verified ? "Yes" : "No"}
      </Tag>
    ),
  },
  {
    title: "Assigned Coupon",
    dataIndex: "has_coupon_assigned",
    key: "has_coupon_assigned",
    render: (text, record) => (
      <>
        <Tag color={record?.has_coupon_assigned ? "green" : "red"}>
          {record?.has_coupon_assigned ? "Yes" : "No"}
        </Tag>
        {record?.assigned_coupon_codes?.map((code) => (
          <Tag color="blue" key={code}>
            {code}
          </Tag>
        ))}
      </>
    ),
  },
  {
    title: "Active",
    dataIndex: "active",
    key: "active",
    render: (text, record) => (
      <>
        <Tag color={record?.active ? "green" : "red"}>
          {record?.active ? "Yes" : "No"}
        </Tag>
      </>
    ),
  },
  {
    title: "Last Login",
    dataIndex: "last_login",
    key: "last_login",
    render: (text, record) =>
      record?.last_login ? formatDate(record.last_login) : "-",
  },
  {
    title: "Source",
    dataIndex: "created_by",
    key: "created_by",
    render: (text) => {
      switch (text) {
        case "signup":
          return "Register";
        case "fb":
          return "FB";
        case "gm":
          return "Gmail";
        case "apple":
          return "Apple";
        default:
          return text || "-";
      }
    },
  },
  {
    title: "Current Active Plan",
    dataIndex: "user_subscription_plan_name",
    key: "user_subscription_plan_name",
    render: (text, record) =>
      record?.user_subscription_plan_name ? record.user_subscription_plan_name : "-",
  },
  // {
  //   title: "Plan Type",
  //   dataIndex: "cost_type",
  //   key: "cost_type",
  //   render: (text) =>
  //     text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "-",
  // },
  {
    title: "Plan Expire",
    dataIndex: "expire_datetime",
    key: "expire_datetime",
    render: (text, record) =>
      record?.expire_datetime ? formatDate(record.expire_datetime) : "-",
  },
  // {
  //   title: "Current Plan Queries Limit",
  //   dataIndex: "plan_limit",
  //   key: "plan_limit",
  //   render: (text, record) =>
  //     record?.plan_limit ? record.plan_limit : "-",
  // },
  // {
  //   title: "Current Plan Used Queries",
  //   dataIndex: "current_used_capped_limit",
  //   key: "current_used_capped_limit",
  //   render: (text, record) =>
  //     record?.current_used_capped_limit ? record.current_used_capped_limit : "-",
  // },
  // {
  //   title: "Current Plan Remaining Queries",
  //   dataIndex: "remaining_capped_limit",
  //   key: "remaining_capped_limit",
  //   render: (text, record) =>
  //     record?.remaining_capped_limit ? record.remaining_capped_limit : "-",
  // },
  // {
  //   title: "Current Plan Total Input Token",
  //   dataIndex: "current_plan_total_input_token",
  //   key: "current_plan_total_input_token",
  //   render: (text, record) =>
  //     record?.current_plan_total_input_token ? record.current_plan_total_input_token : "-",
  // },
  // {
  //   title: "Current Plan Total Output Token",
  //   dataIndex: "current_plan_total_output_token",
  //   key: "current_plan_total_output_token",
  //   render: (text, record) =>
  //     record?.current_plan_total_output_token ? record.current_plan_total_output_token : "-",
  // },
  {
    title: "Total Input Token",
    dataIndex: "total_input_token",
    key: "total_input_token",
    render: (text, record) =>
      record?.total_input_token ? record.total_input_token : "-",
  },
  {
    title: "Total Output Token",
    dataIndex: "total_output_token",
    key: "total_output_token",
    render: (text, record) =>
      record?.total_output_token ? record.total_output_token : "-",
  },
  {
    title: "Total Tokens Used",
    dataIndex: "total_tokens_used",
    key: "total_tokens_used",
    render: (text, record) =>
      record?.total_tokens_used ? record.total_tokens_used : "-",
  },
  // {
  //   title: "Total Capped Usage",
  //   dataIndex: "total_capped_usage",
  //   key: "total_capped_usage",
  //   render: (text, record) =>
  //     record?.total_capped_usage ? record.total_capped_usage : "-",
  // },
  {
    title: "Referral Source",
    dataIndex: "referral_source",
    key: "referral_source",
    render: (text, record) =>
      record?.referral_source ? record.referral_source : "-",
  },
  {
    title: "Created At",
    dataIndex: "created_at",
    key: "created_at",
    render: (text, record) =>
      record?.created_at ? formatDate(record.created_at) : "-",
  },
  {
    title: "Updated At",
    dataIndex: "updated_at",
    key: "updated_at",
    render: (text, record) =>
      record?.updated_at ? formatDate(record.updated_at) : "-",
  },
  {
    title: "Action",
    key: "action",
    fixed: 'right',
    render: (_, record) => {
      const menuItems = [
        {
          key: 'view',
          label: (
            <span onClick={() => handleViewClick(record, 'preference')}>
              <EyeOutlined /> View Preference
            </span>
          ),
        },
        {
          key: 'view',
          label: (
            <span onClick={() => handleViewClick(record, 'travel_preference')}>
              <EyeOutlined /> View Travel Preference
            </span>
          ),
        },
        {
          key: 'edit',
          label: (
            <span onClick={() => handleEditClick(record)}>
              <EditOutlined /> Edit User
            </span>
          ),
        },
        // {
        //   key: 'extend-plan',
        //   label: (
        //     <span
        //       // onClick={() => handlePlanViewClick(record)}
        //       // style={{ color: record?.is_default_plan ? 'gray' : 'inherit', pointerEvents: record?.is_default_plan ? 'none' : 'auto' }}
        //     >
        //       <SolutionOutlined /> Extend Plan Expiry
        //     </span>
        //   ),
        //   // disabled: record?.is_form_submission_plan_switch === "1",
        //   disabled: record.is_form_submission_plan_switch === "1" && (record.is_default_plan === true || record.is_default_plan === null),
        //   onClick: () => handlePlanViewClick(record)
        // },
        // {
        //   key: 'extend-capped',
        //   label: (
        //     <span
        //       // onClick={() => handlePlanCappedClick(record)}
        //       // style={{ color: record?.is_default_plan ? 'gray' : 'inherit', pointerEvents: record?.is_default_plan ? 'none' : 'auto' }}
        //     >
        //       <FormOutlined /> Extend Plan Capped
        //     </span>
        //   ),
        //   // disabled: record?.is_form_submission_plan_switch === "1",
        //   disabled: record.is_form_submission_plan_switch === "1" && (record.is_default_plan === true || record.is_default_plan === null),
        //   onClick: () => handlePlanCappedClick(record)
        // },
        {
          key: 'logs',
          label: (
            <span onClick={() => handleLogClick(record)}>
              <FileSearchOutlined /> View Logs
            </span>
          ),
        },
        // {
        //   key: 'assign-coupon',
        //   label: (
        //     <span onClick={() => handleAssignCouponClick(record)}>
        //       <GiftOutlined /> Assign Coupon
        //     </span>
        //   ),
        // },
        // {
        //   key: 'upgrade',
        //   label: (
        //     <span 
        //     // onClick={() => handlePlanUpgradeViewClick(record)}
        //       // style={{ color: record?.is_form_submission_plan_switch === "1" ? 'gray' : 'inherit', pointerEvents: record?.is_form_submission_plan_switch === "1" ? 'none' : 'auto' }}
        //       >
        //       <DeploymentUnitOutlined /> Plan Upgrade
        //     </span>
        //   ),
        //   disabled: record?.is_form_submission_plan_switch === "1",
        //   onClick: () => handlePlanUpgradeViewClick(record)
        // },
        {
          key: 'toggle',
          label: (
            <span onClick={() => handleToggleActive(record)}>
              <UserOutlined /> User {record?.active ? "Deactivate" : "Activate"}
            </span>
          ),
        },
      ];

      return (
        <div style={{"display": "flex", "gap": 2}}>
          <Button color="primary" variant="link" icon={<TagsOutlined />} onClick={() => handleTagClick(record)}></Button>
          <Dropdown
            overlay={<Menu items={menuItems} />}
            trigger={['click']}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      );
    }
  }

];

const UserList = () => {
  const queryClient = useQueryClient();

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPerferenceType, setPerferenceType] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filters = useSelector((state) => state.filter.filters);
  const [searchTerm, setSearchTerm] = useState("");
  const [isViewModal, setIsViewModal] = useState(false);
  const [isPlanViewModal, setIsPlanViewModal] = useState(false);
  const [isPlanCappedModal, setIsPlanCappedModal] = useState(false);
  const [isPlanUpdateModal, setIsPlanUpdateModal] = useState(false);
  
  const [sortOrder, setSortOrder] = useState("desc")
  const [isLogViewModal, setIsLogViewModal] = useState(false);
  const [isAssignCouponModal, setIsAssignCouponModal] = useState(false);
  const [isMassAssignModal, setIsMassAssignModal] = useState(false);
  const [TagModelVisible, setTagModalVisible] = useState(false);

  // New states for image preview
  const [previewImage, setPreviewImage] = useState(null);
  const [isImagePreviewVisible, setIsImagePreviewVisible] = useState(false);
  const [dateRange, setDateRange] = useState({
              start_date: null,
              end_date: null,
          });

  // Function to open preview
  const handleImageClick = (imageUrl) => {
    setPreviewImage(imageUrl);
    setIsImagePreviewVisible(true);
  };

  // Function to close preview
  const handlePreviewClose = () => {
    setIsImagePreviewVisible(false);
    setPreviewImage(null);
  };

  useEffect(() => {
    setCurrentPage(1); 
  }, [filters, searchTerm, dateRange]);
  
  const skip = (currentPage - 1) * pageSize;


  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["users", currentPage, pageSize, filters, searchTerm, sortOrder, dateRange],
    queryFn: () =>
      getAllUsers({
        skip,
        limit: pageSize,
        name: searchTerm,
        city_ids: filters.city,
        state_ids: filters.state,
        country_ids: filters.country,
        active: filters.active,
        recommendation_ids: filters.preferences,
        order: sortOrder,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        tag_ids: filters.user_tags
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [isModalVisible, setModalVisible] = useState(false);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setModalVisible(true);
  };

  const handleViewClick = (user, type) => {
    setSelectedUser(user);
    setPerferenceType(type);
    setIsViewModal(true);
  };

  const handlePlanViewClick = (user) => {
    setSelectedUser(user);
    setIsPlanViewModal(true);
  };

   const handlePlanCappedClick = (user) => {
    setSelectedUser(user);
    setIsPlanCappedModal(true);
  };

  const handlePlanUpgradeViewClick = (user) => {
    setSelectedUser(user);
    setIsPlanUpdateModal(true);
  };

  const handleLogClick = (user) => {
    setSelectedUser(user);
    setIsLogViewModal(true);
  };

  const handleAssignCouponClick = (user) => {
    setSelectedUser(user);
    setIsAssignCouponModal(true);
  };

  const handleTagClick = (user) => {
    setSelectedUser(user);
    setTagModalVisible(true);
  };

  const mutation = useMutation({
    mutationFn: (data) => updateUser(data.id, data.updatedData),
    onSuccess: (res) => {
      if(res){
        setModalVisible(false)
        queryClient.invalidateQueries(["users"]); // Refetch user list after update
      }
     
    },
  });

  const tagMutation = useMutation({
    mutationFn: (data) => updateUserTag(data.id, data.tagUpdatedData),
    onSuccess: (res) => {
      if(res){
        setTagModalVisible(false)
        queryClient.invalidateQueries(["users"]); // Refetch user list after update
      }
     
    },
  });

  const activateMutation = useMutation({
    mutationFn: (data) => activateDeactivateUser(data.id, data.updatedData),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]); // Refetch user list after update
    },
  });

  const handleUpdateUser = (id, updatedData) => {
    console.log("Updated User Data:", updatedData);
    mutation.mutate({ id, updatedData });
  };

  const handleTagUpdateUser = (id, tagUpdatedData) => {
    console.log('tag update date', id, tagUpdatedData)
    tagMutation.mutate({ id, tagUpdatedData });
  };


  const handleSort = () => {
    setSortOrder((prev) => prev === "asc" ? "desc" : "asc")
  }

  const handleToggleActive = (user) => {
    Modal.confirm({
      title: "Confirm",
      content: `Are you sure you want to ${
        user.active ? "deactivate" : "activate"
      } this user?`,
      okText: user.active ? "Deactivate" : "Activate",
      cancelText: "Cancel",
      onOk: () => {
        activateMutation.mutate({
          id: user.id,
          updatedData: { active: !user.active }, // Toggle Active Status
        });
      },
    });
  };

  const handleDateChange = (dates) => {
      if (dates) {
          setDateRange({
              start_date: dates[0].format("YYYY-MM-DD"),
              end_date: dates[1].format("YYYY-MM-DD"),
          });
      } else {
          setDateRange({
              start_date: null,
              end_date: null,
          });
      }
  };

  const activeDeactiveOptions = () => {
    return [
      {
        label: "Activate",
        value: true
      },
      {
        label: "Deactivate",
        value: false
      }
    ]
  }

  const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
    mutationFn: async () => {
      console.log("✅ Download mutation triggered");
      return await downloadUsers({
        name: searchTerm,
        city_ids: filters.city,
        state_ids: filters.state,
        country_ids: filters.country,
        active: filters.active,
        recommendation_ids: filters.preferences,
        order: sortOrder,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        tag_ids: filters.user_tags
      });
    },
  });
   
  return (
    <div>
      <Flex justify="space-between">
        <h3>Users List</h3>
        <Flex gap="middle">
          {/* <Button type="primary" onClick={() => setIsMassAssignModal(true)}>Bulk Assign Coupon</Button> */}
          <Button type="primary" onClick={triggerDownload} loading={isDownloading}>Export CSV</Button>
        </Flex>
      </Flex>
      <br/>
     
      <FilterContainer>
        <DebouncedInput
          placeholder="Search by Name..."
          value={searchTerm}
          onChange={setSearchTerm}
          delay={1000}
        />
        <FilterSelect
          label="City"
          fetchOptions={fetchCities}
          filterKey="city"
        />
        <FilterSelect
          label="State"
          fetchOptions={fetchStates}
          filterKey="state"
        />
        <FilterSelect
          label="Country"
          fetchOptions={fetchCountries}
          filterKey="country"
        />
        <FilterSelect
          label="Status"
          fetchOptions={activeDeactiveOptions}
          filterKey="active"
        />
         <FilterSelect
          label="Preferences"
          fetchOptions={fetchRecommendations}
          filterKey="preferences"
        />
        <RangePicker onChange={handleDateChange} disabledDate={(current) => current && current.toDate() > new Date()} />
        <FilterSelect
          label="User Tags"
          fetchOptions={getActiveMasterTag}
          filterKey="user_tags"
        />
      </FilterContainer>
      <CustomTable
        columns={columns(handleEditClick, handleToggleActive, handleViewClick, handlePlanViewClick, handlePlanCappedClick,
           handleLogClick, handlePlanUpgradeViewClick, handleSort, sortOrder, handleImageClick, handleTagClick, handleAssignCouponClick)}
        data={data?.users || []}
        loading={isLoading || isFetching}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
          // Size change -> always page 1 + the new limit (sent straight to the
          // query). onShowSizeChange fires natively from the size dropdown, so
          // it's the reliable place to do this; onChange handles plain page nav.
          onShowSizeChange: (_current, size) => {
            setCurrentPage(1);
            setPageSize(size);
          },
          onChange: (page, size) => {
            if (size !== pageSize) {
              setCurrentPage(1);
              setPageSize(size);
            } else {
              setCurrentPage(page);
            }
          },
        }}
        maxHeight={"calc(100vh - 315px)"}
      />
      <EditUserModal
        visible={isModalVisible}
        onClose={() => setModalVisible(false)}
        user={selectedUser}
        onUpdate={handleUpdateUser}
        loading={mutation.isPending}
      />
      <ViewModal
        isModalVisible={isViewModal}
        handleCancel={() => setIsViewModal(false)}
        initialValues={selectedUser}
        type={selectedPerferenceType}
      />
      <PlanViewModal
        isModalVisible={isPlanViewModal}
        handleCancel={() => setIsPlanViewModal(false)}
        initialValues={selectedUser}
      />
      <PlanCappedModal
        isModalVisible={isPlanCappedModal}
        handleCancel={() => setIsPlanCappedModal(false)}
        initialValues={selectedUser}
      />
      <LogViewModal
        isModalVisible={isLogViewModal}
        handleCancel={() => setIsLogViewModal(false)}
        initialValues={selectedUser}
      />
      <AssignCouponModal
        isModalVisible={isAssignCouponModal}
        handleCancel={() => setIsAssignCouponModal(false)}
        initialValues={selectedUser}
      />
      <MassAssignCouponModal
        isModalVisible={isMassAssignModal}
        handleCancel={() => setIsMassAssignModal(false)}
      />
      <PlanUpdateModal
        isModalVisible={isPlanUpdateModal}
        handleCancel={() => setIsPlanUpdateModal(false)}
        initialValues={selectedUser}
      />

      <UserTagModal
        visible={TagModelVisible}
        onClose={() => setTagModalVisible(false)}
        user={selectedUser}
        onUpdate={handleTagUpdateUser}
        loading={tagMutation.isPending}
      />
      
      {/* Image preview modal */}
      <Modal
        open={isImagePreviewVisible}
        footer={null}
        onCancel={handlePreviewClose}
        centered
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <img
            alt="Preview"
            style={{ borderRadius: 10, maxWidth: '90%', maxHeight: '70vh' }}
            src={previewImage}
          />
        </div>
      </Modal>


    </div>
  );
};

export default UserList;
